import { useEffect, useRef, useState } from 'react';
import { useOnionStore } from '../../stores/onionStore';
import { usePlaybackStore } from '../../stores/playbackStore';
import { useSceneStore } from '../../stores/sceneStore';
import { getSpineManager } from '../../lib/spine-manager';

interface GhostFrame {
  dataUrl: string;
  type: 'before' | 'after';
}

/**
 * Renders semi-transparent ghost frames on top of the PixiJS canvas.
 * Only captures when paused — capture is synchronous so no visible flicker.
 */
export default function OnionSkinOverlay() {
  const { enabled, framesBefore, framesAfter, opacity } = useOnionStore();
  const { isPlaying, currentFrame, fps } = usePlaybackStore();
  const { selectedLayerId } = useSceneStore();
  const [ghosts, setGhosts] = useState<GhostFrame[]>([]);
  const captureScheduled = useRef(false);

  useEffect(() => {
    if (!enabled || isPlaying || !selectedLayerId || fps <= 0) {
      setGhosts([]);
      return;
    }

    // Debounce: capture once per frame change (avoid rapid re-triggers during scrub)
    if (captureScheduled.current) return;
    captureScheduled.current = true;

    // Use rAF so PixiJS has a chance to render the current frame first
    requestAnimationFrame(() => {
      captureScheduled.current = false;
      const manager = getSpineManager();
      const currentTime = currentFrame / fps;

      const offsets: number[] = [];
      const frameDur = 1 / fps;
      for (let i = framesBefore; i >= 1; i--) offsets.push(-i * frameDur);
      for (let i = 1; i <= framesAfter; i++) offsets.push(i * frameDur);

      if (offsets.length === 0) { setGhosts([]); return; }

      const captured = manager.captureOnionFrames(selectedLayerId, currentTime, offsets);
      setGhosts(captured);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isPlaying, currentFrame, selectedLayerId, framesBefore, framesAfter, fps]);

  if (!enabled || ghosts.length === 0) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      data-testid="onion-skin-overlay"
      style={{ zIndex: 5 }}
    >
      {ghosts.map((ghost, i) => (
        <img
          key={i}
          src={ghost.dataUrl}
          alt=""
          className="absolute inset-0 w-full h-full"
          style={{
            opacity,
            mixBlendMode: ghost.type === 'before' ? 'multiply' : 'screen',
            // Warm tint for before (orange-red): sepia + hue-rotate
            // Cool tint for after (blue): sepia + hue-rotate to blue
            filter: ghost.type === 'before'
              ? 'sepia(0.6) saturate(1.5) hue-rotate(-30deg)'
              : 'sepia(0.6) saturate(1.5) hue-rotate(150deg)',
          }}
        />
      ))}
    </div>
  );
}
